import endpoints from '@/utils/endpoints.js'
import axios, { cfAxios } from '@/utils/init-axios.js'
import { Role, User } from '@/utils/interfaces'
import moment from 'moment'
import qs from 'qs'
import Vue from 'vue'
import { parse } from '../../../node_modules/vue-currency-input/src/index'
import { ADMIN_MERCHANT_LIST_PARAMS } from '@/utils/endpoints.handwritten.js'
import { setIntegrationTypeCookie } from '@/utils/cookies'
import {
  MerchantSettingsSaveError,
  toDjangoMerchantShape,
  toMerchantRecordUpdate
} from '@/utils/merchant-record.js'
import { toErrorList } from '@/utils/apiErrors'
import { RETRY_QUEUED_TOAST } from '@/utils/failedActionRetry'
import {
  loadStarredReports,
  toggleStarredReport
} from '@/components/Navigation/Sidebar/starred-reports'
import { queueBucketWrite, readPreferenceBucket } from '@/utils/ui-preference'

/**
 * The merchant asked for does not exist, or is not this user's. Start-up recovery reads the type,
 * never the message.
 */
export class MerchantNotFoundError extends Error {
  constructor(message) {
    super(message)
    this.name = 'MerchantNotFoundError'
  }
}

/** An axios error alone does not say why; the status is what identifies it. */
function httpFailureDetail(error) {
  const status = error?.response?.status

  return status ? `HTTP ${status}` : error?.message || 'network error'
}

function starredReportsPort({ commit, getters }) {
  // Resolved per call, not captured: a write can still be queued when the user switches merchant, and
  // the old tenant path is rejected once the token's merchantId claim has rotated.
  const url = () => {
    const merchantId = getters.preferenceMerchantId

    return merchantId ? endpoints.uiPreference(merchantId) : ''
  }

  return {
    canPersist: () => Boolean(getters.preferenceMerchantId),
    read: () => readPreferenceBucket(url()),
    write: async bucket => (await cfAxios.put(url(), bucket)).data || bucket,
    exclusive: queueBucketWrite,
    current: () => getters.starredReports || [],
    setStarred: keys => commit('setStarredReports', keys),
    setBucket: bucket => commit('setUiPreferences', bucket),
    onLoadError: error =>
      console.error(
        `Starred reports: load failed (${httpFailureDetail(error)}) ${url()}`
      ),
    onSaveError: error => {
      console.error(
        `Starred reports: save failed (${httpFailureDetail(error)}) ${url()}`
      )
      Vue.toasted.show('Could not save your starred reports', {
        duration: 4000,
        position: 'bottom-center'
      })
    }
  }
}

/**
 * The list window narrowed to its `count`.
 *
 * Spelled out rather than spread from {@link ADMIN_MERCHANT_LIST_PARAMS}: the two share a filter
 * but not a purpose, and a caller reading this should not have to hold the list's sort in mind to
 * see what the count asks for. Omitting `sort` does not avoid an ORDER BY — the resource's
 * `defaultSort` applies either way — it just says that no order was wanted.
 */
const ADMIN_MERCHANT_COUNT_PARAMS = {
  isActive__exact: 'true',
  limit: 1,
  page: 1
}

/**
 * Whether the gateway will admit this user to the admin scope. The `super_admin` ROLE, not the `super`
 * group. Django mints the Firebase token's `user_type: 'super'` — which is what the gateway's admin
 * scope admits — from the prbac role slugs (`paybytxt/services/auth_service.py` `firebase_identity`),
 * while `rootGetters.isSuper` reads the `groups` claim, which comes from `user.groups.all()`.
 */
function isSuperTenant(rootGetters) {
  return Boolean(rootGetters.accessClaims?.roles?.includes('super_admin'))
}

/**
 * How many merchants the signed-in user may switch between.
 *
 * A merchant user is 1 without asking: Django's `User.organization` is a single FK, and the admin
 * list is super-only at the gateway, so the request would be a 403 rather than an answer.
 */
async function countMerchants(rootGetters) {
  if (!isSuperTenant(rootGetters)) {
    return 1
  }

  try {
    const res = await cfAxios.get(endpoints.adminMerchants(), {
      params: ADMIN_MERCHANT_COUNT_PARAMS
    })

    return res.data.count
  } catch (error) {
    // This number decides whether the navbar offers a merchant switcher, and nothing else. The gateway is
    // reached with the Firebase token, whose mint Django treats as non-fatal, so it can be missing — and
    // cfAxios then sends the Django JWT, which the gateway refuses.
    console.error('Merchant count unavailable; hiding the switcher.', error)

    return 1
  }
}

/**
 * The pkid core-service addresses a merchant by, for the organization UUID the app holds. A merchant
 * user has one organization: the token's merchant claim carries its pkid, and the profile's
 * `organization.pkid` is the backup when the claim is absent. For a super user, the caller's hint,
 * then the current merchant when it is the one asked for; otherwise the admin list answers it.
 */
async function resolveMerchantPkid(
  { dispatch, rootGetters },
  merchantID,
  pkid
) {
  if (!isSuperTenant(rootGetters)) {
    if (rootGetters.tokenMerchantId) {
      // The getter answers a string, and other readers want it so; every branch here is a number.
      return Number(rootGetters.tokenMerchantId)
    }

    if (!rootGetters.profile) {
      await dispatch('getProfile')
    }

    const profilePkid = rootGetters.profile?.organization?.pkid

    if (profilePkid == null) {
      throw new Error('The profile carries no organization pkid.')
    }

    return profilePkid
  }

  if (pkid != null) {
    return pkid
  }

  const current = rootGetters.currentMerchant

  if (current?.id === merchantID && current.pkid != null) {
    return current.pkid
  }

  const res = await cfAxios.get(endpoints.adminMerchants(), {
    params: { id__exact: merchantID, limit: 1 }
  })
  const row = res.data.results?.[0]

  if (row?.pkid == null) {
    throw new MerchantNotFoundError(`No merchant with id ${merchantID}.`)
  }

  return row.pkid
}

/**
 * One merchant, in the shape Django's retrieve returned. Commits nothing. Without a Firebase token the
 * gateway would refuse the request, so Django's retrieve answers instead — decided before any call,
 * never as a fallback from a failed one (c.28973). With a token, every failure of core's read rejects,
 * including core's 502 when it cannot answer the BlytzCollect flags.
 */
async function readMerchantRecord(ctx, { merchantID, pkid }) {
  if (!ctx.rootGetters.firebaseToken) {
    const res = await axios.get(
      endpoints.retrieveMerchantOrganization(merchantID)
    )

    return res.data
  }

  const merchantPkid = await resolveMerchantPkid(ctx, merchantID, pkid)
  const res = await cfAxios.get(endpoints.merchantRecord(merchantPkid))
  const merchant = toDjangoMerchantShape(res.data)

  if (merchant.id !== merchantID) {
    throw new MerchantNotFoundError(
      `Merchant ${merchantPkid} is not ${merchantID}.`
    )
  }

  return merchant
}

/**
 * Saves the General Settings form and answers the merchant in Django's retrieve shape.
 * @throws {MerchantSettingsSaveError} on any failure of core's PUT; Django's errors reject unchanged.
 */
async function saveMerchantSettings(
  ctx,
  { merchantID, pkid, settings, superUser }
) {
  // Decided before any call, never as a fallback from a failed one, as for readMerchantRecord.
  if (!ctx.rootGetters.firebaseToken) {
    const res = await axios.patch(
      endpoints.updateMerchantOrganization(merchantID),
      settings
    )

    return res.data
  }

  try {
    const merchantPkid = await resolveMerchantPkid(ctx, merchantID, pkid)
    // A merchant user's pkid is the token's, whatever the form holds; never write it onto another.
    if (pkid != null && Number(pkid) !== Number(merchantPkid)) {
      throw new MerchantSettingsSaveError(
        'These settings are for another merchant. Reload the page and try again.'
      )
    }
    const res = await cfAxios.put(
      endpoints.merchantRecord(merchantPkid),
      toMerchantRecordUpdate(settings, { superUser })
    )

    return toDjangoMerchantShape(res.data)
  } catch (error) {
    if (error instanceof MerchantSettingsSaveError) {
      throw error
    }
    if (!error?.response) {
      console.error('Merchant settings save failed before a response:', error)
    }
    throw new MerchantSettingsSaveError(
      toErrorList(error?.response?.data?.message)[0]
    )
  }
}

/** A location read or report save failed with a message meant for the user. */
export class LocationRequestError extends Error {
  constructor(message) {
    super(message)
    this.name = 'LocationRequestError'
  }
}

const EMAIL_REPORT_TYPES = ['DEPOSIT', 'ACHR']

async function resolveLocationPkid(ctx, merchantID, locationID, knownPkid) {
  const findPkid = locations =>
    locations?.find(location => location.id === locationID)?.pkid

  const merchantPkid = await resolveMerchantPkid(ctx, merchantID)
  let locationPkid = knownPkid ?? findPkid(ctx.rootGetters.locations)
  if (locationPkid == null) {
    const current = ctx.rootGetters.currentMerchant
    if (current?.id === merchantID) {
      await ctx.dispatch('getLocations', current)
      locationPkid = findPkid(ctx.rootGetters.locations)
    } else {
      // Another merchant's list must not land in the store, which every screen reads as the current one's.
      for (let page = 1; locationPkid == null; page++) {
        const res = await cfAxios.get(
          endpoints.merchantLocations(merchantPkid),
          { params: { page, pageSize: 500 } }
        )
        const rows = res.data?.results ?? []
        locationPkid = findPkid(rows)
        if (rows.length < 500 || page * 500 >= (res.data?.count ?? 0)) {
          break
        }
      }
    }
  }
  if (locationPkid == null) {
    throw new LocationRequestError(
      'This location could not be found for this merchant.'
    )
  }

  return { merchantPkid, locationPkid }
}

function toDjangoLocationShape(location) {
  return {
    name: location.name,
    phone_number: location.phoneNumber,
    address: location.address,
    city: location.city,
    state_province: location.stateProvince,
    postal_code: location.postalCode,
    incomm_biller_id: location.incommBillerId
  }
}

// Without a Firebase token Django answers both reads, decided before any call as for
// readMerchantRecord. `reportIds` are Django's row ids, which only the tokenless save needs.
/**
 * Reads one location and its Deposit and ACH Return recipients for the location detail screen.
 * @param {object} ctx - the action context
 * @param {object} payload - the route's ids
 * @param {string} payload.merchantID - the merchant UUID
 * @param {string} payload.locationID - the location UUID
 * @returns {Promise<{location: object, reports: {DEPOSIT: ?string, ACHR: ?string}, locationPkid: ?number, reportIds: object}>}
 * @throws {LocationRequestError} with a token, when the route's location is not the merchant's
 */
async function readLocationDetail(ctx, { merchantID, locationID }) {
  if (!ctx.rootGetters.firebaseToken) {
    const [locationRes, reportsRes] = await Promise.all([
      axios.get(endpoints.retrieveMerchantLocation(merchantID, locationID)),
      axios.get(endpoints.listEmailReportings(merchantID, locationID))
    ])
    const reports = { DEPOSIT: null, ACHR: null }
    const reportIds = {}
    for (const row of reportsRes.data.results) {
      if (
        row.merchant_location === locationID &&
        EMAIL_REPORT_TYPES.includes(row.report)
      ) {
        reports[row.report] = row.recipients
        reportIds[row.report] = row.id
      }
    }

    return {
      location: locationRes.data,
      reports,
      locationPkid: null,
      reportIds
    }
  }

  const { merchantPkid, locationPkid } = await resolveLocationPkid(
    ctx,
    merchantID,
    locationID
  )
  const [locationRes, reportsRes] = await Promise.all([
    cfAxios.get(endpoints.merchantLocation(merchantPkid, locationPkid)),
    cfAxios.get(endpoints.locationEmailReports(merchantPkid, locationPkid))
  ])

  return {
    location: toDjangoLocationShape(locationRes.data),
    reports: { DEPOSIT: reportsRes.data.DEPOSIT, ACHR: reportsRes.data.ACHR },
    locationPkid,
    reportIds: {}
  }
}

/**
 * Saves a location's Deposit and ACH Return recipients: one core PUT, or Django without a token.
 * @param {object} ctx - the action context
 * @param {object} payload - the save
 * @param {string} payload.merchantID - the merchant UUID
 * @param {string} payload.locationID - the location UUID
 * @param {?number} payload.locationPkid - skips the store lookup when known
 * @param {object} payload.reports - `{ DEPOSIT, ACHR }` recipient strings
 * @param {object} payload.reportIds - Django row ids from readLocationDetail, tokenless only
 * @returns {Promise<object>} the saved `{ reports, reportIds }`; `reportIds` are Django's, tokenless only
 * @throws {LocationRequestError} on any failure of core's PUT; Django's errors reject with `reportIds` added
 */
async function saveLocationEmailReports(
  ctx,
  { merchantID, locationID, locationPkid, reports, reportIds = {} }
) {
  if (!ctx.rootGetters.firebaseToken) {
    const savedIds = { ...reportIds }
    try {
      for (const report of EMAIL_REPORT_TYPES) {
        const body = {
          merchant_location: locationID,
          report,
          recipients: reports[report]
        }
        if (reportIds[report]) {
          await axios.patch(
            endpoints.partialUpdateEmailReporting(
              merchantID,
              locationID,
              reportIds[report]
            ),
            body
          )
        } else {
          const res = await axios.post(
            endpoints.createEmailReporting(merchantID, locationID),
            body
          )
          savedIds[report] = res.data?.id
        }
      }
    } catch (error) {
      // A row created before the failure must be PATCHed, not POSTed again, on the next save.
      error.reportIds = savedIds
      throw error
    }

    return { reports, reportIds: savedIds }
  }

  try {
    const resolved = await resolveLocationPkid(
      ctx,
      merchantID,
      locationID,
      locationPkid
    )
    const res = await cfAxios.put(
      endpoints.locationEmailReports(
        resolved.merchantPkid,
        resolved.locationPkid
      ),
      { DEPOSIT: reports.DEPOSIT, ACHR: reports.ACHR }
    )

    return { reports: res.data, reportIds: {} }
  } catch (error) {
    if (error instanceof LocationRequestError) {
      throw error
    }
    if (!error?.response) {
      console.error(
        'Location email reports save failed before a response:',
        error
      )
    }
    throw new LocationRequestError(
      toErrorList(error?.response?.data?.message)[0]
    )
  }
}

export default {
  /**
   * Reads a merchant without making it the current one — for a screen that shows a merchant the
   * user picked. `getMerchant` is the action that switches.
   */
  readMerchantRecord,
  saveMerchantSettings,
  readLocationDetail,
  saveLocationEmailReports,
  async getMerchant({ commit, dispatch, rootGetters }, { merchantID, pkid }) {
    const appliedMerchantID =
      merchantID || localStorage.getItem('current_merchant_id')

    if (appliedMerchantID) {
      const [merchant, numMerchants] = await Promise.all([
        readMerchantRecord(
          { dispatch, rootGetters },
          { merchantID: appliedMerchantID, pkid }
        ),
        countMerchants(rootGetters)
      ])

      setIntegrationTypeCookie(merchant.integration_type)
      commit('setCurrentMerchant', { merchant })
      commit('setNumMerchants', { numMerchants })
      localStorage.setItem('current_merchant_id', merchant.id)

      await Promise.allSettled([
        dispatch('getLocations', merchant),
        dispatch('fetchMerchantAccounts', merchant),
        dispatch('getAccountTags')
      ])

      return merchant
    }

    // No merchant to load yet. Resolve WHICH merchant, then re-enter by id rather than commit anything
    // here: the branch above is the only place that commits, so the app sees one shape (the mapped record,
    // snake_case) and the dependants are dispatched once.
    if (isSuperTenant(rootGetters)) {
      // The same list the switcher reads, narrowed to its first row: only that row's `id` and `pkid` are
      // used here, and `count` still answers the empty case. Asking for the full page would pull up to a
      // thousand rows over the wire on every cold login to read one row.
      let res

      try {
        res = await cfAxios.get(endpoints.adminMerchants(), {
          params: { ...ADMIN_MERCHANT_LIST_PARAMS, limit: 1 }
        })
      } catch (error) {
        console.error(
          'Could not resolve a default merchant from the admin list.',
          error
        )

        return null
      }

      if (res.data.count === 0) {
        return res
      }

      const [first] = res.data.results

      return dispatch('getMerchant', { merchantID: first.id, pkid: first.pkid })
    }

    // The profile carries this user's one organization. `main.ts` fires `getProfile` and this
    // action inside the same `Promise.all`, so it may not have landed — awaited here rather than
    // assumed, and skipped when the store already holds it.
    if (!rootGetters.profile) {
      await dispatch('getProfile')
    }

    const organizationId = rootGetters.profile?.organization?.id

    if (!organizationId) {
      return null
    }

    return dispatch('getMerchant', { merchantID: organizationId })
  },
  async fetchAiBatchConfigs(
    { commit },
    { merchantID, page = 1, pageSize = 100 }
  ) {
    if (!merchantID) {
      commit('setAiBatchConfigs', [])
      commit('setAiBatchConfigsCount', 0)

      return []
    }

    commit('setAiBatchConfigsLoading', true)
    try {
      const response = await axios.get(
        endpoints.listAiBatchConfigs(merchantID),
        {
          params: {
            page,
            page_size: pageSize
          }
        }
      )
      const data = response.data || {}
      const results = data.results ?? data
      commit('setAiBatchConfigs', results ?? [])
      commit(
        'setAiBatchConfigsCount',
        typeof data.count === 'number'
          ? data.count
          : Array.isArray(results)
            ? results.length
            : 0
      )

      return results
    } catch (error) {
      console.error('Error loading AI caller batch configs:', error)
      commit('setAiBatchConfigs', [])
      commit('setAiBatchConfigsCount', 0)
      throw error
    } finally {
      commit('setAiBatchConfigsLoading', false)
    }
  },
  async fetchAiCallerAgents({ commit }, { merchantID }) {
    if (!merchantID) {
      commit('setAiCallerAgents', [])

      return []
    }

    commit('setAiCallerAgentsLoading', true)
    try {
      const response = await axios.get(
        endpoints.listAiCallerAgents(merchantID),
        {
          params: { page_size: 100 }
        }
      )
      const data = response.data || {}
      const results = data.results ?? data
      commit('setAiCallerAgents', results ?? [])

      return results
    } catch (error) {
      console.error('Error loading AI caller agents:', error)
      commit('setAiCallerAgents', [])
      throw error
    } finally {
      commit('setAiCallerAgentsLoading', false)
    }
  },
  async fetchAiBatchRuns(
    { commit },
    { merchantID, page = 1, pageSize = 25, filters = {} }
  ) {
    if (!merchantID) {
      commit('setAiCallerBatches', [])
      commit('setAiCallerBatchesCount', 0)

      return []
    }

    commit('setAiCallerBatchesLoading', true)
    try {
      const response = await axios.get(endpoints.listAiBatchRuns(merchantID), {
        params: {
          page,
          page_size: pageSize,
          ...filters
        }
      })
      const data = response.data || {}
      const results = data.results ?? data
      commit('setAiCallerBatches', results ?? [])
      commit(
        'setAiCallerBatchesCount',
        typeof data.count === 'number'
          ? data.count
          : Array.isArray(results)
            ? results.length
            : 0
      )

      return data
    } catch (error) {
      console.error('Error loading AI caller batch history:', error)
      commit('setAiCallerBatches', [])
      commit('setAiCallerBatchesCount', 0)
      throw error
    } finally {
      commit('setAiCallerBatchesLoading', false)
    }
  },
  async fetchAiBatchRunsProgress({ commit }, { merchantID, batchIds = [] }) {
    const ids = Array.isArray(batchIds)
      ? batchIds.filter(id => id !== null && id !== undefined)
      : []

    if (!merchantID || ids.length === 0) {
      return []
    }

    try {
      const response = await axios.get(
        endpoints.listAiBatchRunsProgress(merchantID),
        {
          params: {
            batch_ids: ids.join(',')
          }
        }
      )
      const progressData = response.data ?? []
      commit('mergeAiCallerBatchProgress', progressData)

      return progressData
    } catch (error) {
      console.error('Error loading AI caller batch progress:', error)
      throw error
    }
  },
  async createAiBatchConfig({ dispatch, getters }, { merchantID, payload }) {
    const appliedMerchantId = merchantID || getters.currentMerchant?.id
    if (!appliedMerchantId) {
      throw new Error('Missing merchant ID')
    }
    await axios.post(endpoints.createAiBatchConfig(appliedMerchantId), payload)
    await dispatch('fetchAiBatchConfigs', { merchantID: appliedMerchantId })
  },
  async updateAiBatchConfig(
    { dispatch, getters },
    { merchantID, id, payload }
  ) {
    const appliedMerchantId = merchantID || getters.currentMerchant?.id
    if (!appliedMerchantId || !id) {
      throw new Error('Missing merchant ID or config ID')
    }
    await axios.patch(
      endpoints.updateAiBatchConfig(appliedMerchantId, id),
      payload
    )
    await dispatch('fetchAiBatchConfigs', { merchantID: appliedMerchantId })
  },
  async deleteAiBatchConfig({ dispatch, getters }, { merchantID, id }) {
    const appliedMerchantId = merchantID || getters.currentMerchant?.id
    if (!appliedMerchantId || !id) {
      throw new Error('Missing merchant ID or config ID')
    }
    await axios.delete(endpoints.destroyAiBatchConfig(appliedMerchantId, id))
    await dispatch('fetchAiBatchConfigs', { merchantID: appliedMerchantId })
  },
  async getLocations({ commit }, merchant) {
    // pkid, not the UUID: core keys the tenant root on an integer FK, so a UUID there is a type
    // error rather than an empty result.
    const merchantPkid = merchant?.pkid
    if (!merchantPkid) {
      // A merchant was supplied and we cannot query it: this is a switch to a merchant whose pkid has not
      // arrived. Keeping the previous merchant's locations would show one tenant's data under another's name
      // in every location-scoped screen, so clear instead.
      if (merchant) {
        commit('setLocations', [])
        commit('setCurrentLocation', undefined)
      }

      return
    }

    const response = await cfAxios.get(
      endpoints.merchantLocations(merchantPkid),
      {
        // 500 is core's maximum, five times the 100 Django silently capped these at.
        params: { page: 1, pageSize: 500 }
      }
    )

    // Undefined here would read as "nothing loaded yet" everywhere downstream.
    const locations = response.data?.results ?? []

    commit('setLocations', locations)
    // Still "lowest pkid": core's default order is pkid ascending, so no client-side sort.
    commit('setCurrentLocation', locations[0])
  },
  async getAccountTags({ commit, getters }) {
    const response = await axios.get(
      endpoints.listAccountTags(getters.currentMerchant.id),
      {
        params: { page: 1, page_size: 40 }
      }
    )
    commit('setAccountTags', response.data?.results ?? [])
  },
  async getUsers({ commit }, { merchant, querystring }) {
    const response = await axios.get(endpoints.listUsers(merchant.id), {
      params: querystring
    })
    const users = response.data.results.map(user => new User(user))
    commit('setUsers', users)
  },
  async getMerchantPage({ commit }, { params }) {
    commit('setMerchantLoading', true)
    if ('search' in params) {
      params['search'] = params['search'].trim()
    }
    try {
      const res = await axios.get(
        endpoints.listMerchantOrganizationsSimplified(),
        { params: params }
      )
      const numMerchants = res.data.count
      const merchants = res.data.results
      commit('setNumMerchants', { numMerchants })
      commit('setMerchantPage', { merchants })

      return res
    } finally {
      commit('setMerchantLoading', false)
    }
  },
  async getMerchantReportURL({ commit, state }, { reportType }) {
    commit('setTransactionReportURL', { reportURL: '' })
    const res = await axios.get(endpoints.listtransaction_report_urls(), {
      params: {
        account_id: state.currentMerchantAccount.id,
        report_type: reportType
      }
    })
    const reportURL = res.data.url
    commit('setTransactionReportURL', { reportURL })

    return res
  },
  setPageTitle({ commit }, pageTitle) {
    commit('setPageTitle', [...pageTitle])
  },
  async getOauthLinks({ commit }) {
    const res = await axios.get(endpoints.listOauthLinks())
    const oauthLinks = res.data
    commit('setOauthLinks', oauthLinks)

    return res
  },
  async getCustomer(
    { state, commit },
    { customerID, checkIntegration = false }
  ) {
    const merchantID = state.currentMerchant.id
    try {
      commit('setCustomerLoading', true)
      const response = await axios.get(
        endpoints.retrieveCustomer(merchantID, customerID),
        {
          params: { checkIntegration: checkIntegration }
        }
      )
      const customer = response.data
      commit('setCurrentCustomer', customer)

      axios.post(endpoints.updateLastViewed(merchantID, customerID))

      return customer
    } finally {
      commit('setCustomerLoading', false)
    }
  },
  async getFailedActions({ commit }, { merchantID, page_size = 100 }) {
    const response = await axios.get(endpoints.listFailedActions(merchantID), {
      params: { page_size }
    })
    const failedActions = response.data.results
    commit('setFailedActions', failedActions)

    return failedActions
  },
  async markActionResolved(
    { dispatch },
    { merchantID, actionID, refresh = false }
  ) {
    const response = await axios.post(
      endpoints.resolveFailedAction(merchantID, actionID)
    )
    Vue.toasted.show('Marked Action as Resolved', {
      duration: 2000,
      position: 'bottom-center'
    })
    if (refresh) {
      dispatch('getFailedActions', { merchantID })
    }

    return response
  },
  async retryAction({ dispatch }, { merchantID, actionID, refresh = false }) {
    const response = await axios.post(
      endpoints.retryFailedAction(merchantID, actionID)
    )
    // The repost is published to a broker, so there is no post result to report here.
    Vue.toasted.show(RETRY_QUEUED_TOAST, {
      duration: 6000,
      position: 'bottom-center'
    })
    if (refresh) {
      dispatch('getFailedActions', { merchantID })
    }

    return response
  },
  async openReceiptModal({ commit }, payment) {
    try {
      commit('setCurrentPayment', payment)
      // If no errors fetching open modal
      commit('setReceiptModalVisibility', true)
    } catch {
      // pass
    }
  },
  async getTransactions({ state }, { page, size, statuses, range, like }) {
    const merchantID = state.currentMerchant.id
    const filteredStatuses = statuses
      .filter(status => status.selected)
      .map(status => status.value)
    const response = await axios.get(
      endpoints.listTransactionReport(merchantID),
      {
        params: {
          page: page,
          page_size: size,
          status: filteredStatuses,
          start: range.start,
          end: range.end,
          like: like
        },
        paramsSerializer: params => {
          return qs.stringify(params, { arrayFormat: 'repeat' })
        }
      }
    )

    return response
  },
  async getTransactionExport({ state }, { page, size, range, statuses, like }) {
    const merchantID = state.currentMerchant.id
    const filteredStatuses = statuses
      .filter(status => status.selected)
      .map(status => status.value)
    const response = await axios.post(
      endpoints.exportTransactionReport(merchantID),
      {
        params: {
          page_number: page,
          page_size: size,
          status: filteredStatuses,
          start: range.start,
          end: range.end,
          like: like
        },
        paramsSerializer: function (params) {
          return qs.stringify(params, { arrayFormat: 'repeat' })
        }
      }
    )

    return response
  },
  async refundPayment({ state }, { payment, total, sources }) {
    const merchantID = state.currentMerchant.id
    const response = await axios.post(endpoints.refundPayment(merchantID), {
      payment: payment,
      total: total,
      sources: sources
    })

    return response
  },
  async cancelPayment({ state }, { paymentID }) {
    const merchantID = state.currentMerchant.id
    const response = await axios.post(endpoints.cancelPayment(merchantID), {
      payment: paymentID
    })

    return response
  },
  async voidLoggedPayment({ state }, { paymentID }) {
    const merchantID = state.currentMerchant.id
    // No request body: void_logged is all-or-nothing on the payment in the URL. Errors are left to
    // propagate so the caller sees the backend's readable refusal message (not today / not most recent /
    // invoice touched) rather than a generic failure.
    const response = await axios.post(
      endpoints.voidLoggedPayment(merchantID, paymentID)
    )

    return response
  },
  async refundTransaction({ state }, { transactionID }) {
    const merchantID = state.currentMerchant.id
    const response = await axios.post(endpoints.refundTransaction(merchantID), {
      transaction: transactionID
    })

    return response
  },
  async fetchAccountPromises({ commit, state }) {
    if (!state?.currentMerchant?.id || !state?.currentAccount?.id) return
    commit('setAccountPromisesLoading', true)
    try {
      const response = await axios.get(
        endpoints.getAccountPromises(
          state.currentMerchant.id,
          state.currentAccount.id
        )
      )
      const promises = response.data.filter(
        promise => moment(promise.scheduled_date).diff(moment()) > 1
      )
      commit('setAccountPromises', promises)
    } finally {
      commit('setAccountPromisesLoading', false)
    }
  },
  async fetchMerchantAccounts({ commit }, { id }) {
    commit('setMerchantAccountsLoading', true)
    try {
      const response = await axios.get(endpoints.listMerchantAccounts(id))
      commit('setMerchantAccounts', response.data)
      commit('setCurrentMerchantAccount', response.data.results[0])

      return response
    } finally {
      commit('setMerchantAccountsLoading', false)
    }
  },
  async fetchBinInfo({ commit }, { merchantID, bin }) {
    commit('setBinInfoLoading', true)
    try {
      const response = await axios.get(
        endpoints.retrieveBinInfo(merchantID, bin)
      )
      commit('setBinInfo', response.data)

      return response
    } finally {
      commit('setBinInfoLoading', false)
    }
  },
  async fetchRole({ commit }, { merchantID, roleID }) {
    commit('setRoleLoading', true)
    try {
      const response = await axios.get(endpoints.roleDetail(merchantID, roleID))
      const role = new Role()
      role.description = response.data.role_description
      role.name = response.data.role_name
      role.settings = response.data.role_settings
      role.settings.max_merchant_payment_amount = parse(
        role.settings.max_merchant_payment_amount,
        {}
      )
      role.settings.min_merchant_payment_amount = parse(
        role.settings.min_merchant_payment_amount,
        {}
      )
      for (const permission in role.permissions) {
        const each = response.data.has_access.find(has => {
          return permission === has.name
        })
        if (each) role.permissions[permission] = true
      }
      commit('setRole', role)
    } finally {
      commit('setRoleLoading', false)
    }
  },

  async getMostRecentPayment(
    { commit },
    { merchantID, customerID, accountID }
  ) {
    commit('setRecentPaymentLoading', true)
    try {
      const response = await axios.get(
        endpoints.listCustomerAccountTransactions(
          merchantID,
          customerID,
          accountID
        ),
        { params: { page: 1, page_size: 50 } }
      )
      const defaultDateFormat = 'YYYY-MM-DD'
      const currentDate = moment().format(defaultDateFormat)
      const data = response.data?.results
        .filter(item => {
          return (
            ['PAID', 'REFUND'].includes(item.status) &&
            moment(item.created).format(defaultDateFormat) === currentDate
          )
        })
        .sort(
          (before, after) =>
            new Date(moment(before.created)) - new Date(moment(after.created))
        )
        .map(item => {
          return {
            created: item.created,
            id: item.id,
            total_amount: item.payment.total_amount
          }
        })
      if (!data.length) return {}

      return data[0]
    } finally {
      commit('setRecentPaymentLoading', false)
    }
  },
  async voidCustomer({ state, commit }, customerID) {
    const merchantID = state.currentMerchant.id
    try {
      commit('setCustomerLoading', true)
      const response = await axios.delete(
        endpoints.voidCustomer(merchantID, customerID)
      )

      return response
    } finally {
      commit('setCustomerLoading', false)
    }
  },
  getDashboardDateRange({ commit }, dateRange) {
    commit('setDashboardDateRange', dateRange)
  },
  async getAvailableUpc() {
    const response = await axios.get(endpoints.listAvailableUpc(), {})

    return response
  },
  async getPropayBusinessTypes() {
    const response = await axios.get(endpoints.listPropayBusinessTypes(), {})

    return response
  },
  async updateCustomerBpInformation({ state, commit }, { customerUserData }) {
    const merchantId = state.currentMerchant.id
    const customerId = state.currentCustomer.id
    try {
      commit('setCustomerLoading', true)
      const response = await axios.put(
        endpoints.customerBpInformation(merchantId, customerId),
        customerUserData
      )

      return response
    } finally {
      commit('setCustomerLoading', false)
    }
  },
  async emailIsRegistered(_context, userData) {
    const outcome = { ok: false }
    try {
      await axios.post(endpoints.validateUserEmail(), userData)
    } catch (err) {
      const data = err.response?.data || []
      var defaultError = 'Email could not be validated'
      if (data.length > 0) defaultError = data[0]
      Object.assign(outcome, {
        ok: true,
        error: defaultError
      })
    }

    return outcome
  },
  // starred reports live under `starred_reports` in the same per-user
  // ui-preference bucket the customer-list column preset uses.
  async fetchStarredReports(context) {
    await loadStarredReports(starredReportsPort(context))
  },
  async toggleStarredReport(context, reportKey) {
    await toggleStarredReport(starredReportsPort(context), reportKey)
  }
}
